import { Response } from '@/types';
import { Action } from '@/constants/ajax';
import { UrlHelper } from '@/helpers/UrlHelper';
import { CancelledResponse, Reason } from '@/models/CancelledResponse';
import { ServerResponse } from '@/models/ServerResponse';
import { CustomError } from '@/models/CustomError';
import i18n from '@/i18n';
import { LanguageCodeByBranchCode } from '@/helpers/i18n';
import { CookieHelper } from '@/helpers/CookieHelper';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import axios from 'axios';
import { updateTmscCookieAndStore } from '@/helpers/TmscHelper';
import store from '@/store';
/**
 * @class AjaxService Handles ajax calls
 */
export default class AjaxService {
    private readonly proxyRoute: string;
    private readonly apiUrl: string;
    private readonly formRoute: string;
    private readonly smsRoute: string;
    private readonly documentRoute: string;
    public readonly RESOURCES: any;
    public readonly SAME_TOKEN_ACTION_LIST: any;

    private validationRequests: Map<string, Promise<Response>> = new Map<
        string,
        Promise<Response>
    >();
    private readonly state: any;

    constructor(state: any) {
        this.state = state;
        const token = UrlHelper.getQueryParam('token');

        if (token) {
            this.setToken(token);
        }
        this.apiUrl = process.env.VUE_APP_API_URL as string;

        // set up base proxy route
        this.proxyRoute = this.apiUrl + 'realform/';

        // <editor-fold desc="Register available endpoints">
        this.formRoute =
            this.PROXY_ROUTE + 'form-' + this.state.config.version + '/';
        this.smsRoute = this.PROXY_ROUTE + 'sms/';
        this.documentRoute = this.PROXY_ROUTE + 'document/';

        this.RESOURCES = {
            [Action.GET_ADDRESS]: this.FORM_ROUTE + 'get-postal-code-addresses',
            [Action.ABORT_MOBILE]: this.FORM_ROUTE + 'skip-desktop-waiting',
            [Action.DICTIONARIES]: this.FORM_ROUTE + 'get-dictionaries-list',
            [Action.GET_TOKEN]: this.FORM_ROUTE + 'get-token',
            [Action.VALIDATE]: this.FORM_ROUTE + 'validate',
            [Action.NEW_ACCOUNT]: this.FORM_ROUTE + 'new-account',
            [Action.LOGIN]: this.FORM_ROUTE + 'new-login',
            [Action.DATA_UPLOAD_CHOICE]: this.FORM_ROUTE + 'data-upload-choice',
            [Action.SELECT_MOBILE_DESKTOP]:
                this.FORM_ROUTE + 'mobile-desktop-choice',
            [Action.DESKTOP_DOCUMENT_SELECTION]:
                this.FORM_ROUTE + 'desktop-document-choice',
            [Action.CHECK_IF_DESKTOP_CAN_PROCEED]:
                this.FORM_ROUTE + 'mobile-desktop-is-completed',
            [Action.DESKTOP_DOCUMENT_UPLOAD]:
                this.DOCUMENT_ROUTE + 'desktop-document-upload',
            [Action.MOBILE_SMS]: this.SMS_ROUTE + 'mobile-sms',
            [Action.DESKTOP_SMS]: this.SMS_ROUTE + 'desktop-sms',
            [Action.CONFIRM_SMS_CODE]: this.FORM_ROUTE + 'desktop-sms-code',
            [Action.MOBILE_DOCUMENT_SELECTION]:
                this.DOCUMENT_ROUTE + 'mobile-document-choice',
            [Action.MOBILE_DOCUMENT_UPLOAD]:
                this.DOCUMENT_ROUTE + 'mobile-document-upload',
            [Action.MOBILE_RETURN_TO_DESKTOP]:
                this.FORM_ROUTE + 'mobile-return-to-desktop',
            [Action.PERSONAL_DATA_CONFIRM]:
                this.FORM_ROUTE + 'personal-data-confirm',
            [Action.ADDRESS_DETAILS]: this.FORM_ROUTE + 'address-details',
            [Action.ACCOUNT_TYPE]: this.FORM_ROUTE + 'choose-account-type',
            [Action.TAX_OFFICE_DETAILS]: this.FORM_ROUTE + 'tax-office-details',
            [Action.CURRENCY]: this.FORM_ROUTE + 'currency-choice',
            [Action.RISK_ASSESSMENT]: this.FORM_ROUTE + 'risk-assessment',
            [Action.DECLARATION_ACCEPTANCE]:
                this.FORM_ROUTE + 'declaration-acceptance',
            [Action.CLINT_ZONE_LOGIN]: this.FORM_ROUTE + 'errors-report',
            [Action.PAYMENT]: this.FORM_ROUTE + 'summary-and-payment-choice',
            [Action.ACCOUNT_OPEN]: this.FORM_ROUTE + 'account-open',
            [Action.UPLOAD_SUMMARY]: this.FORM_ROUTE + 'upload-summary',
            [Action.UPLOAD_TRY_AGAIN]: this.FORM_ROUTE + 'upload-try-again',
            [Action.MOBILE_SMS_CODE]: this.FORM_ROUTE + 'mobile-sms-code',
            [Action.VERIFY_EMAIL]: this.FORM_ROUTE + 'verify-email',
            [Action.CONFIRM_EMAIL]: this.FORM_ROUTE + 'confirm-email',
            [Action.SKIP_HYBRID_UPLOAD]: this.FORM_ROUTE + 'skip-hybrid-upload',
            [Action.MOBILE_QR_CODE_SCAN]:
                this.FORM_ROUTE + 'mobile-qr-code-scan',
            [Action.GET_IDSCAN_TOKEN]: this.DOCUMENT_ROUTE + 'get-idscan-token',
            [Action.NOTIFY_JOURNEY]: this.DOCUMENT_ROUTE + 'notify-journey',
            [Action.WRITE_ANALYTICS]: this.apiUrl + 'dc',
            [Action.GET_ALLOWED_ACCOUNT_TYPES]:
                this.FORM_ROUTE + 'get-allowed-account-types',
            [Action.GET_ACCOUNT_TYPE]: this.FORM_ROUTE + 'get-account-type',
            [Action.EXISTING_ACCOUNT]: this.FORM_ROUTE + 'existing-account',
            [Action.GET_CLIENT_COUNTRY]: this.FORM_ROUTE + 'get-client-country'
        };
        // </editor-fold>

        this.SAME_TOKEN_ACTION_LIST = {
            [Action.VALIDATE]: this.FORM_ROUTE + 'validate',
            [Action.NEW_ACCOUNT]: this.FORM_ROUTE + 'new-account',
            [Action.LOGIN]: this.FORM_ROUTE + 'new-login',
            [Action.DICTIONARIES]: this.FORM_ROUTE + 'get-dictionaries-list',
            [Action.MOBILE_SMS]: this.SMS_ROUTE + 'mobile-sms',
            [Action.MOBILE_SMS_CODE]: this.FORM_ROUTE + 'mobile-sms-code',
            [Action.MOBILE_DOCUMENT_SELECTION]:
                this.DOCUMENT_ROUTE + 'mobile-document-choice',
            [Action.MOBILE_DOCUMENT_UPLOAD]:
                this.DOCUMENT_ROUTE + 'mobile-document-upload',
            [Action.MOBILE_RETURN_TO_DESKTOP]:
                this.FORM_ROUTE + 'mobile-return-to-desktop',
            [Action.CHECK_IF_DESKTOP_CAN_PROCEED]:
                this.FORM_ROUTE + 'mobile-desktop-is-completed',
            [Action.SKIP_HYBRID_UPLOAD]: this.FORM_ROUTE + 'skip-hybrid-upload',
            [Action.GET_ADDRESS]: this.FORM_ROUTE + 'get-postal-code-addresses',
            [Action.SELECT_MOBILE_DESKTOP]:
                this.FORM_ROUTE + 'mobile-desktop-choice',
            [Action.MOBILE_QR_CODE_SCAN]:
                this.FORM_ROUTE + 'mobile-qr-code-scan',
            [Action.GET_IDSCAN_TOKEN]: this.DOCUMENT_ROUTE + 'get-idscan-token',
            [Action.NOTIFY_JOURNEY]: this.DOCUMENT_ROUTE + 'notify-journey',
            [Action.WRITE_ANALYTICS]: this.apiUrl + 'dc',
        };
    }

    /**
     * Make GET request to specified url
     * @param url
     * @return Promise<Response>
     */
    public async get(url: string): Promise<Response> {
        const headers = {
            'Content-Type': 'application/json',
        };

        const { data } = await axios.get(url, { headers });

        if (data.globalError || (data.error && !Array.isArray(data.error))) {
            throw new CustomError(
                data.globalError || data.error,
                data.next === 'restart'
            );
        }

        return new ServerResponse(data);
    }

    /**
     * Make POST request to specified url
     * @param url
     * @param data Object containing data that should be sent
     * @param customHeaders Object containing request headers override
     *
     * @return ServerResponse
     *
     * @throws GlobalError
     */
    public async post(
        url: string,
        data: any = {},
        customHeaders: any = {}
    ): Promise<Response> {
        let headers = {
            'Content-Type': 'application/json',
        };
        headers = Object.assign(headers, customHeaders);

        if (!this.getToken()) {
            await this.requestToken();
            data.token = this.getToken();
        }

        const { data: responseData } = await axios.post(url, data, { headers });

        if (
            responseData.token &&
            !Object.values(this.SAME_TOKEN_ACTION_LIST).includes(url)
        ) {
            this.setToken(responseData.token);
        }

        if (
            responseData.globalError ||
            (responseData.error && !Array.isArray(responseData.error))
        ) {
            throw new CustomError(
                responseData.globalError || responseData.error,
                responseData.next === 'restart'
            );
        }

        return new ServerResponse(responseData);
    }

    /**
     * Makes a validation request to the backend.
     *
     * Can validate a single field or multiple fields in one request.
     *
     * @param nameOrFields - Either:
     *   - the name of a single field to validate, or
     *   - an object containing multiple field–value pairs to validate at once.
     * @param value - The value to validate (only used when validating a single field).
     * @returns Promise<Response> - A single response object containing validation results.
     */

    public async validate(
        nameOrFields: string | Record<string, any>,
        value?: any
    ): Promise<Response> {
        const fields =
            typeof nameOrFields === 'string'
                ? { [nameOrFields]: value }
                : { ...nameOrFields };
        this.enrichTinValidationWithTaxCountry(fields);

        const promise = this.post(this.RESOURCES[Action.VALIDATE], {
            ...fields,
            token: this.getToken(),
            languageCode:
                LanguageCodeByBranchCode[
                    i18n.global.locale.value
                ].toUpperCase(),
            branchCode: i18n.global.locale.value.toUpperCase(),
        });

        const requestKey =
            typeof nameOrFields === 'string' ? nameOrFields : 'multi';

        this.validationRequests.set(requestKey, promise);

        try {
            let response: Response = await promise;
            if (promise !== this.validationRequests.get(requestKey)) {
                response = new CancelledResponse(Reason.RACE);
            }
            return response;
        } catch (err) {
            throw err;
        } finally {
            if (this.validationRequests.get(requestKey) === promise) {
                this.validationRequests.delete(requestKey);
            }
        }
    }

    /**
     * Send GBG Status
     * @param payload object
     */
    public async sendGBGStatus(payload: any) {
        try {
            const response = await fetch(this.apiUrl + 'site/gbg-stats', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...payload, token: this.getToken() }),
            });

            if (!response.ok) {
                console.warn(
                    'GBG status not sent, server responded with:',
                    response.status
                );
            }
        } catch (error) {
            console.warn('GBG status could not be sent:', error);
        }
    }

    /**
     * Verify token from URL
     * @param token string
     */
    public async verifyToken(token: string): Promise<Response> {
        this.setToken(token);
        try {
            const response = await this.validate('token', token);
            return response;
        } catch (err) {
            console.error('Error ', err);
            this.restart();
            throw err;
        }
    }

    /**
     * Make request to registered endpoint
     * @param action Endpoint name, must be registered in constructor
     * @param payload Data, may be JS object or FormData instance
     * @param headers Request headers override
     */
    public async runAction(action: Action, payload: any, headers: any = {}) {
        let data: FormData | Record<string, any> | null = null;

        const cookies = CookieHelper.getCookie('_ga');
        let _ga = '';
        if (cookies) {
            _ga = cookies.split('.').slice(-2).join('.');
        }
        if (payload instanceof FormData) {
            data = payload;
            data.append('token', this.getToken() as string);
            data.append(
                'languageCode',
                LanguageCodeByBranchCode[i18n.global.locale.value].toUpperCase()
            );
            data.append(
                'branchCode',
                i18n.global.locale.value === 'en'
                    ? 'EU'
                    : i18n.global.locale.value.toUpperCase()
            );
            data.append('cookie', _ga);
        } else if (action == 7) {
            let cookieGtmParams: string[] | null = null;

            function getCookie(name: string): string | null {
                function escape(s: any) {
                    return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1');
                }
                const match = document.cookie.match(
                    RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)')
                );
                return match ? match[1] : null;
            }

            const cookieHldl = getCookie('_ga_HLDL6TSEJ9');
            if (cookieHldl && cookies) {
                cookieGtmParams = cookieHldl.split('.');
            }
            const cookie6pg = getCookie('_ga_6PG6QGNWP6');
            if (cookie6pg && cookies) {
                cookieGtmParams = cookie6pg.split('.');
            }

            updateTmscCookieAndStore(store, [
                'gclid',
                'gbraid',
                'wbraid',
                'msclkid',
                'gclsrc',
                'adid',
                'utm_source',
                'utm_medium',
                'utm_campaign',
                'utm_content',
                'utm_term',
                'utm_id',
                'cmids',
                'saids',
                'app_instance',
                'gadid',
                'uuid',
                'idfv',
                'idfa',
                'referrer',
            ]);

            const getTmscCookieAsJson = (): Record<string, any> | null => {
                const tmscCookie = document.cookie
                    .split('; ')
                    .find((row) => row.startsWith('tmsc='));

                if (tmscCookie) {
                    try {
                        const tmscValue = decodeURIComponent(
                            tmscCookie.split('=')[1]
                        );
                        return JSON.parse(tmscValue);
                    } catch (error) {
                        console.error('Cookie parse error:', error);
                    }
                }

                return null;
            };
            const tmscData = getTmscCookieAsJson();
            data = {
                ...payload,
                ...(tmscData ? { tmsc: tmscData } : {}),
                token: this.getToken(),
                session_count: cookieGtmParams ? cookieGtmParams[1] : null,
                session_id: cookieGtmParams ? cookieGtmParams[2] : null,
                isOanda: UtilityHelper.isOandaPath(),
                languageCode:
                    LanguageCodeByBranchCode[
                        i18n.global.locale.value
                    ].toUpperCase(),
                branchCode:
                    i18n.global.locale.value === 'en'
                        ? 'EU'
                        : i18n.global.locale.value.toUpperCase(),
                client_id: _ga,
                cookie: _ga,
            };
        } else {
            data = {
                ...payload,
                token: this.getToken(),
                isOanda: UtilityHelper.isOandaPath(),
                languageCode:
                    LanguageCodeByBranchCode[
                        i18n.global.locale.value
                    ].toUpperCase(),
                branchCode:
                    i18n.global.locale.value === 'en'
                        ? 'EU'
                        : i18n.global.locale.value.toUpperCase(),
                cookie: _ga,
            };
        }

        // else {
        //   data = {
        //     ...payload,
        //     token: this.getToken(),
        //     isOanda: UtilityHelper.isOandaPath(),
        //     languageCode: LanguageCodeByBranchCode[i18n.global.locale.value].toUpperCase(),
        //     branchCode: i18n.global.locale.value === "en" ? "EU" : i18n.global.locale.value.toUpperCase(),
        //     cookie: _ga,
        //   };
        // }

        const response = await this.post(this.RESOURCES[action], data, headers);

        // if response contains analytics data then save them
        if ((response as ServerResponse).analytics) {
            this.state.dataLayerService.setData(
                (response as any).analytics.userId,
                (response as any).analytics.leadId
            );
            //GA4 new
            this.state.analyticsService.setData({
                transaction_id: (response as any).analytics.leadId,
                userId: (response as any).analytics.userId,
                transaction_id_sha: (response as any).analytics.leadIdSha,
                userIdSha: (response as any).analytics.userIdSha,
            });
        }

        return response;
    }

    /**
     * Retrieve new session token from server
     */
    public async requestToken() {
        const headers = {
            'Content-Type': 'application/json',
        };

        const { data } = await axios.post(
            this.RESOURCES[Action.GET_TOKEN],
            {},
            { headers }
        );

        if (data.token) {
            this.setToken(data.token);
        }
    }

    /**
     * Get session token
     */
    public getToken() {
        return window.localStorage.getItem(this.TOKEN);
    }

    /**
     * Set session token
     * @param token
     */
    private setToken(token: string) {
        window.localStorage.setItem(this.TOKEN, token);
    }

    /**
     * Remove token and restart form session
     */
    public restart() {
        window.localStorage.removeItem(this.TOKEN);
    }

    // <editor-fold desc="Getters">

    public get API_URL() {
        return this.apiUrl;
    }

    public get PROXY_ROUTE() {
        return this.proxyRoute;
    }

    public get SMS_ROUTE() {
        return this.smsRoute;
    }

    public get FORM_ROUTE() {
        return this.formRoute;
    }

    public get DOCUMENT_ROUTE() {
        return this.documentRoute;
    }

    private get TOKEN() {
        return '4bdc12c9-69a9-4308-a520-a05d90edd8e2';
    }

    public getPendingValidations(): Promise<Response>[] {
        return Array.from(this.validationRequests.values());
    }

    private enrichTinValidationWithTaxCountry(fields: Record<string, any>) {
        Object.keys(fields).forEach((fieldName) => {
            if (!fieldName.toLowerCase().endsWith('tinnumber')) {
                return;
            }

            let taxCountryFieldName = '';
            if (fieldName.endsWith('TinNumber')) {
                taxCountryFieldName = fieldName.replace(
                    /TinNumber$/,
                    'TaxCountry'
                );
            } else if (fieldName.endsWith('tinNumber')) {
                taxCountryFieldName = fieldName.replace(
                    /tinNumber$/,
                    'taxCountry'
                );
            } else {
                taxCountryFieldName = fieldName.replace(
                    /tinnumber$/i,
                    'taxCountry'
                );
            }

            if (
                Object.prototype.hasOwnProperty.call(
                    fields,
                    taxCountryFieldName
                )
            ) {
                return;
            }

            const formData = (this.state && this.state.formData) || {};
            fields[taxCountryFieldName] = formData[taxCountryFieldName] ?? null;
        });
    }

    // </editor-fold>
}
